import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");
const goteborgKommunerText = getCityMunicipalityList("göteborg");

// Tömma dödsbo per stad — /tomma-dodsbo-stockholm och /tomma-dodsbo-goteborg.
// Vinkel som föräldern /tomma-dodsbo: hela processen att tömma ett dödsbo
// (sortera värdesaker och dokument, spara/skänka/återvinna, sopat till
// mäklare/BRF/hyresvärd, nyckelöverlämning, anhöriga på distans).
// Transportvinkeln bor på /bortforsling-dodsbo, dit båda sidorna länkar.
// Städning ingår inte i sortimentet: sidorna lovar tömt och sopat, inte städat.
export const tommaDodsboOrterPages: ServicePageConfig[] = [
  {
    slug: "tomma-dodsbo-stockholm",
    serviceCategory: "tömningar",
    city: "stockholm",

    meta: {
      title: "Tömma dödsbo Stockholm – Varsamt & fast pris | Ungdomskraft",
      description:
        "Tömning av dödsbo i Stockholm. Vi sorterar, sparar det viktiga, skänker och återvinner – från Södermalm till Bromma och Nacka. Fast pris, boka online.",
      h1: "Tömma dödsbo Stockholm",
    },

    hero: {
      heading: "Tömma dödsbo i Stockholm",
      intro:
        "Vi hjälper er att tömma dödsbo i hela Stockholm – en bostadsrätt på Södermalm eller i Vasastan, en hyresrätt i Hägersten eller Farsta, eller en villa i Bromma, Älvsjö eller Nacka. Vi går igenom hemmet med er, lägger undan det som ska sparas, skänker det som kan användas igen och lämnar bostaden tom och sopad till mäklaren, föreningen eller hyresvärden. Ni bestämmer takten.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft bär ut bohag vid tömning av dödsbo i en Stockholmslägenhet",
    },

    featureCards: {
      headingText: "Vi sköter tömningen av dödsboet i Stockholm",
      sinceText: "Dödsbotömning sedan 2014",
      bookYourText: "Boka tömning av dödsbo i Stockholm",
      bookDescText:
        "Ange adressen, beskriv bostaden och välj en tid. Du ser priset direkt – ingen offertförfrågan, inget hembesök som krävs.",
      vehicleTitleText: "Rätt bil för Stockholms gator",
      vehicleDescText:
        "Skåpbil som tar sig fram i innerstadens trånga kvarter och in på villagatorna i Bromma och Älvsjö. Vi planerar lastning och parkering innan vi kommer.",
    },

    itemsSection: {
      heading: "Det här tar vi hand om i dödsboet",
      items: [
        {
          name: "Möbler & bohag",
          description: "Soffor, sängar, skåp, bord och allt löst i hemmet",
          image: "/sofa.webp",
          alt: "Soffa och bohag som bärs ut vid tömning av dödsbo på Södermalm i Stockholm",
        },
        {
          name: "Kläder & textilier",
          description: "Garderober med kläder, sänglinne, handdukar och mattor",
          image: "/bedding.webp",
          alt: "Kläder och sänglinne som sorteras för Stadsmissionen vid dödsbotömning i Stockholm",
        },
        {
          name: "Husgeråd & porslin",
          description: "Serviser, glas, bestick, kastruller och prydnadsföremål",
          image: "/dishes.webp",
          alt: "Porslin och husgeråd som packas vid tömning av dödsbo i Vasastan, Stockholm",
        },
        {
          name: "Vitvaror",
          description: "Kylskåp, frys, tvättmaskin och diskmaskin som ska ut",
          image: "/fridge.webp",
          alt: "Kylskåp som kopplas loss vid tömning av dödsbo i en villa i Bromma",
        },
        {
          name: "Dokument & foton",
          description: "Allt som ser personligt eller viktigt ut läggs i märkta kartonger",
          image: "/box.webp",
          alt: "Märkta kartonger med dokument och fotoalbum från ett dödsbo i Hägersten, Stockholm",
        },
        {
          name: "Källare, vind & förråd",
          description: "Föreningens källar- och vindsförråd töms i samma uppdrag",
          image: "/tv.webp",
          alt: "TV och elektronik från ett källarförråd vid dödsbotömning i Stockholm",
        },
      ],
    },

    seoContent: {
      mainHeading: "Tömning av dödsbo i Stockholm – med omtanke och en tydlig plan",
      mainText:
        "Att tömma ett dödsbo i Stockholm hamnar ofta mitt i en redan tung tid. Bouppteckningen ska förrättas inom tre månader från dödsfallet och sedan lämnas till Skatteverket, en bostadsrätt ska kanske säljas eller en hyresrätt lämnas tillbaka – och samtidigt står ett helt hem kvar som någon ska gå igenom. Många anhöriga bor i en annan del av landet, eller i andra änden av regionen, och hinner inte vara på plats i flera dagar. Det är där vi kommer in: vi tar hand om själva tömningen av dödsboet så att ni kan lägga er tid på det som bara ni kan göra.\n\nStockholms bostäder ställer sina egna krav. I sekelskifteshusen på Södermalm, Östermalm och i Vasastan är hissarna ofta små och trapporna svängda. I trevåningshusen från 1940- och 50-talet i Hammarbyhöjden, Hägerstensåsen och Traneberg finns ofta ingen hiss alls, och vindsförråden ligger högst upp. I en villa i Bromma, Älvsjö eller Enskede är det istället garage, källare och trädgårdsbod som tar tid. Vi räknar med allt det redan när ni bokar och skickar en skåpbil och två personer som är vana vid just de här husen.\n\nInnan något bärs ut går vi igenom hemmet – tillsammans med er på plats, eller via bilder och telefon om ni bor långt bort. Fotoalbum, brev, smycken, kontanter, bankpapper och annat som kan behövas för bouppteckningen lägger vi i märkta kartonger. Möbler, kläder och husgeråd i gott skick kan köras till Stadsmissionen, Myrorna eller Erikshjälpen. Resten sorteras och lämnas på godkänd återvinning. När vi går därifrån är bostaden tom och sopad, redo för mäklarens fotograf, föreningens besiktning eller nyckelöverlämningen till hyresvärden.",
      preparationHeading: "Så förbereder ni tömningen av dödsboet",
      preparationText:
        "Bestäm i familjen vad som ska sparas innan vi kommer: smycken, foton, konst, en särskild möbel. Plocka undan det eller sätt en tydlig lapp på det. Allt annat kan ni lämna som det är – vi sorterar på plats.\n\nSamla nycklar och taggar till allt som hör till bostaden: porttagg, källar- och vindsförråd, cykelrum, garage och eventuell tvättstuga. I många Stockholmsföreningar är förråden numrerade, så ta reda på vilket nummer som gäller. Kan ingen av er vara där? Lämna nycklarna hos en granne, mäklaren eller föreningens förvaltare.\n\nBerätta vid bokningen om det finns ett datum att hålla, till exempel en visning, en besiktning eller den dag hyresvärden vill ha nycklarna. Nämn också om porten har kod, om det finns hiss och om det går att stanna med bilen utanför. Farligt avfall som färgburkar, kemikalier och batterier kan vi inte ta med, men det lämnar ni utan kostnad på Stockholm Vatten och Avfalls återvinningscentraler, till exempel Bromma, Östberga eller Vantör.",
      additionalSections: [
        {
          heading: "Där vi tömmer dödsbon i Stockholm",
          content:
            "Vi tömmer dödsbon i hela Stockholms stad och i kranskommunerna. I innerstaden – Södermalm, Kungsholmen, Vasastan, Östermalm och Norrmalm – handlar det ofta om bostadsrätter som ska säljas, där mäklaren vill ha tomt inför fotograferingen. Vi planerar lastningen efter gatan: en innergård, en lastzon eller en kort stund utanför porten.\n\nI söderort (Årsta, Enskede, Hägersten, Farsta, Bandhagen) och västerort (Bromma, Vällingby, Hässelby, Spånga) möter vi både lägenheter utan hiss och villor med vind och garage. I kranskommuner som Nacka, Solna, Sundbyberg, Huddinge, Täby och Lidingö gäller samma pris – inga tillägg för området.\n\nEtt dödsbo sträcker sig ofta utanför själva lägenheten. Vi tömmer källarförrådet, vindsförrådet och garageplatsen i samma bokning, så att föreningen eller hyresvärden får tillbaka allt på en gång.",
        },
        {
          heading: "Tömma dödsbo eller bortforsling av dödsbo?",
          content:
            "Två tjänster som låter lika men gör olika saker. Tömning av dödsbo, den här sidan, är hela processen: vi går igenom hemmet, lägger undan värdesaker och dokument, sorterar vad som ska sparas, skänkas och återvinnas, och lämnar en tom och sopad bostad. Passar när ingen i familjen har hunnit sortera, eller när ni vill att någon annan tar ansvar för helheten.\n\nBortforsling av dödsbo är transporten: ni har redan bestämt vad som ska bort, och vi bär ut och kör iväg det. Passar när familjen har gått igenom allt själv och bara behöver hjälp med de tunga lyften och körningen.",
          cta: { text: "Läs om bortforsling av dödsbo", href: "/bortforsling-dodsbo" },
        },
        {
          heading: "Varför tömma dödsboet i Stockholm med Ungdomskraft?",
          listItems: [
            {
              label: "Ingenting försvinner utan ert ja –",
              text: "Hittar vi brev, foton, kontanter eller papper som ser viktiga ut hör vi av oss innan vi går vidare. Allt personligt hamnar i märkta kartonger.",
            },
            {
              label: "Vi känner Stockholms hus –",
              text: "Smala spiraltrappor på Söder, trevåningshus utan hiss i Hägerstensåsen, villor med fulla garage i Bromma. Vi vet vad som tar tid och planerar därefter.",
            },
            {
              label: "Följ arbetet på distans –",
              text: "Bor ni i Göteborg, Umeå eller utomlands? Boka online, lämna nycklarna hos någon ni litar på och få bilder och uppdateringar under dagen.",
            },
            {
              label: "Skänkt i första hand –",
              text: "Det som kan användas igen går till Stadsmissionen, Myrorna eller Erikshjälpen. Går bohaget till återanvändning kan ni ha rätt till RUT-avdrag på arbetskostnaden.",
            },
            {
              label: "Fast pris och försäkrat –",
              text: "Ni ser priset när ni bokar. Alla uppdrag är försäkrade genom Trygg-Hansa, så trapphus, hiss och bostad är skyddade under hela tömningen.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar det att tömma ett dödsbo i Stockholm?",
        answer:
          "Det beror på hur mycket som finns i bostaden och hur lång tid det tar. Ett garage eller ett par rum brukar motsvara paketet Extra Stor (4 995 kr, upp till 4 timmars arbete). En hel lägenhet motsvarar Mega Stor (8 995 kr, upp till 8 timmar). En stor villa med vind och källare kan behöva mer tid än så. Körsträcka inom 20 km ingår. Avfallsavgifter debiteras separat. Du ser priset direkt när du bokar.",
      },
      {
        question: "Hur skiljer sig tömning av dödsbo från bortforsling av dödsbo?",
        answer:
          "Vid tömning av dödsbo tar vi hand om hela processen: genomgång av värdesaker och dokument, sortering, donation och återvinning, och en tom och sopad bostad till sist. Bortforsling av dödsbo är transporten av det ni redan har sorterat. Osäker på vilket ni behöver? Beskriv läget när ni bokar så hjälper vi er.",
      },
      {
        question: "Gör ni dödsbostädning i Stockholm?",
        answer:
          "Vi tömmer bostaden helt och sopar ur den när allt är ute, så att den är redo för nästa steg. Flyttstädning ingår inte i vår tjänst. Ska lägenheten storstädas inför visning eller överlämning kan ni boka en städfirma till dagen efter, så har de en tom bostad att jobba i.",
      },
      {
        question: "Kan ni tömma dödsboet om vi bor utanför Stockholm?",
        answer:
          "Ja, det gör vi ofta. Ni bokar online, skickar gärna bilder på bostaden och lämnar nycklarna hos en granne, mäklaren eller föreningens förvaltare. Under dagen får ni bilder och uppdateringar, och hittar vi något som kan vara värdefullt ringer vi innan vi går vidare.",
      },
      {
        question: "Hur hanterar ni värdesaker och dokument?",
        answer:
          "Vi går igenom lådor, skåp och garderober och lägger undan allt som kan ha värde för er eller för bouppteckningen – foton, brev, smycken, kontanter, bankpapper och försäkringsbrev. Det hamnar i märkta kartonger som ni hämtar. Ingenting kastas utan ert besked.",
      },
      {
        question: "Tömmer ni källar- och vindsförråd i föreningen?",
        answer:
          "Ja, i samma uppdrag. Många Stockholmsföreningar har numrerade förråd i källaren eller på vinden, ibland även cykelrum och garageplatser. Ange vilka utrymmen som hör till bostaden när ni bokar, och se till att vi har nycklar eller tagg.",
      },
      {
        question: "Klarar ni lägenheter utan hiss?",
        answer:
          "Ja. I trevåningshusen i Hammarbyhöjden, Hägerstensåsen och Traneberg finns ofta ingen hiss, och i innerstadens sekelskifteshus är hissen ofta för liten för en soffa. Vi bär i trapporna, monterar isär det som behövs och skyddar väggar och golv. Inga tillägg för våningsplan.",
      },
      {
        question: "Kan ni samordna med mäklaren eller hyresvärden?",
        answer:
          "Ja. Berätta vid bokningen när mäklarens fotograf kommer, när föreningen ska besiktiga eller när hyresvärden vill ha nycklarna, så lägger vi tömningen därefter. Vi kan också lämna nycklarna till mäklaren eller en granne när vi är klara, om ni kommer överens om det i förväg.",
      },
      {
        question: "Vart tar bohaget vägen efter tömningen?",
        answer:
          "Möbler, kläder och husgeråd i gott skick körs till en secondhand eller välgörenhetsorganisation, till exempel Stadsmissionen, Myrorna eller Erikshjälpen. Elektronik och vitvaror lämnas till godkänd mottagning. Resten sorteras på godkänd återvinning. Vill familjen ha särskilda saker levererade till någon av er går det att lägga till som ett extra stopp.",
      },
      {
        question: "Kan jag få RUT-avdrag när ni tömmer dödsboet?",
        answer:
          "Om bohaget transporteras till en secondhand-butik eller organisation för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Avdraget hanteras direkt på fakturan, så du behöver aldrig ligga ute med pengarna. Vanlig tömning där allt körs direkt till återvinning ger inte rätt till avdrag.",
      },
      {
        question: "Hur lång tid tar det att tömma ett dödsbo i Stockholm?",
        answer:
          "En etta eller tvåa i innerstaden tar oftast en halv till en hel dag, beroende på hur mycket som finns och hur lätt det är att komma till med bilen. En villa med garage, källare och vind kan ta två dagar eller mer. Vi kan också dela upp tömningen i etapper om familjen vill gå igenom saker mellan gångerna.",
      },
      {
        question: "Hur snart kan ni börja?",
        answer:
          "Ofta redan samma vecka som ni hör av er. Många familjer vill ha tid att gå igenom de personliga sakerna först, och det anpassar vi oss efter. Har ni ett hyresavtal som löper ut eller en visning inbokad, säg det när ni bokar så prioriterar vi datumet.",
      },
      {
        question: "Tar ni med farligt avfall från dödsboet?",
        answer:
          "Nej, färg, kemikalier, lösningsmedel, batterier och asbest kan vi inte transportera. Som privatperson lämnar du det utan kostnad på Stockholm Vatten och Avfalls återvinningscentraler, till exempel Bromma, Lövsta, Östberga eller Vantör.",
      },
      {
        question: "Vilka kommuner runt Stockholm tömmer ni dödsbon i?",
        answer: `Vi finns i hela Stockholmsregionen: ${stockholmKommunerText}. Samma pris i alla kommuner, inga tillägg för området.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Tömma dödsbo i Stockholm i 3 steg",
      step2Text:
        "Vi går igenom bostaden rum för rum, lägger undan det personliga och bär ut resten genom trapphus och hiss. Det som kan användas igen skänks, resten återvinns.",
      ctaHeading: "Boka tömning av dödsbo i Stockholm",
      ctaText:
        "Ange adressen, beskriv bostaden och välj en dag som passar familjen. Du ser priset direkt – vi tar hand om resten med omtanke.",
    },

    en: {
      slug: "house-clearance-stockholm",
      meta: {
        title: "House Clearance Stockholm – Estate Clearing | Ungdomskraft",
        description:
          "Estate clearance in Stockholm. We sort, keep what matters, donate and recycle – from Södermalm to Bromma and Nacka. Fixed price, book online.",
        h1: "House Clearance Stockholm",
      },
      hero: {
        heading: "Estate clearance in Stockholm",
        intro:
          "We help you clear an estate anywhere in Stockholm – a co-op flat on Södermalm or in Vasastan, a rental in Hägersten or Farsta, or a house in Bromma, Älvsjö or Nacka. We go through the home with you, set aside what should be kept, donate what can be used again and leave the property empty and swept for the estate agent, the housing association or the landlord. You set the pace.",
        heroImageAlt:
          "Ungdomskraft carrying out household items during an estate clearance in a Stockholm apartment",
      },
      itemsSection: {
        heading: "What we take care of in the estate",
        items: [
          {
            name: "Furniture & household",
            description: "Sofas, beds, cabinets, tables and everything loose in the home",
            image: "/sofa.webp",
            alt: "Sofa and household goods carried out during an estate clearance on Södermalm, Stockholm",
          },
          {
            name: "Clothes & textiles",
            description: "Wardrobes of clothes, bed linen, towels and rugs",
            image: "/bedding.webp",
            alt: "Clothes and bed linen sorted for Stadsmissionen during an estate clearance in Stockholm",
          },
          {
            name: "Kitchenware & china",
            description: "Dinner sets, glasses, cutlery, pans and ornaments",
            image: "/dishes.webp",
            alt: "China and kitchenware packed during an estate clearance in Vasastan, Stockholm",
          },
          {
            name: "Appliances",
            description: "Fridge, freezer, washing machine and dishwasher",
            image: "/fridge.webp",
            alt: "Fridge disconnected during an estate clearance in a house in Bromma",
          },
          {
            name: "Documents & photos",
            description: "Anything that looks personal or important goes into labelled boxes",
            image: "/box.webp",
            alt: "Labelled boxes of documents and photo albums from an estate in Hägersten, Stockholm",
          },
          {
            name: "Basement, attic & storage",
            description: "The association's basement and attic storage is cleared in the same job",
            image: "/tv.webp",
            alt: "TV and electronics from a basement storage unit during an estate clearance in Stockholm",
          },
        ],
      },
      seoContent: {
        mainHeading: "Estate clearance in Stockholm – with care and a clear plan",
        mainText:
          "Clearing an estate in Stockholm usually lands in the middle of an already difficult time. The estate inventory (bouppteckning) has to be held within three months of the death and then submitted to the Swedish Tax Agency, a co-op flat may need to be sold or a rental handed back – and meanwhile a whole home is still there for someone to go through. Many relatives live in another part of the country, or at the other end of the region, and cannot be on site for days. That is where we come in: we handle the clearance itself so you can spend your time on what only you can do.\n\nStockholm's homes set their own demands. In the turn-of-the-century buildings on Södermalm, Östermalm and in Vasastan the lifts are often small and the stairs curved. In the three-storey blocks from the 1940s and 50s in Hammarbyhöjden, Hägerstensåsen and Traneberg there is often no lift at all, and the attic storage sits at the very top. In a house in Bromma, Älvsjö or Enskede it is the garage, basement and garden shed that take time. We account for all of that when you book and send a van and two people who know these buildings.\n\nBefore anything is carried out we go through the home – with you on site, or via photos and phone if you live far away. Photo albums, letters, jewellery, cash, bank papers and anything else needed for the estate inventory go into labelled boxes. Furniture, clothes and kitchenware in good condition can go to Stadsmissionen, Myrorna or Erikshjälpen. The rest is sorted and delivered to approved recycling. When we leave, the property is empty and swept, ready for the estate agent's photographer, the association's inspection or the key handover to the landlord.",
        preparationHeading: "How to prepare for the estate clearance",
        preparationText:
          "Agree within the family on what should be kept before we arrive: jewellery, photos, art, a particular piece of furniture. Put it aside or label it clearly. Everything else can stay as it is – we sort on site.\n\nGather keys and fobs for everything that belongs to the home: entrance fob, basement and attic storage, bike room, garage and laundry room. In many Stockholm associations the storage units are numbered, so find out which number applies. Can none of you be there? Leave the keys with a neighbour, the estate agent or the association's property manager.\n\nTell us when booking if there is a date to meet, such as a viewing, an inspection or the day the landlord wants the keys back. Also mention if the entrance has a code, whether there is a lift and whether the van can stop outside. We cannot take hazardous waste such as paint tins, chemicals and batteries, but you can drop it off free of charge at Stockholm Vatten och Avfall's recycling centres, for example Bromma, Östberga or Vantör.",
        additionalSections: [
          {
            heading: "Where we clear estates in Stockholm",
            content:
              "We clear estates across the City of Stockholm and the surrounding municipalities. In the inner city – Södermalm, Kungsholmen, Vasastan, Östermalm and Norrmalm – it is often a co-op flat that is going on the market, and the estate agent wants it empty before the photos. We plan the loading around the street: a courtyard, a loading zone or a short stop outside the door.\n\nIn the southern suburbs (Årsta, Enskede, Hägersten, Farsta, Bandhagen) and the western suburbs (Bromma, Vällingby, Hässelby, Spånga) we meet both flats without a lift and houses with an attic and garage. In neighbouring municipalities such as Nacka, Solna, Sundbyberg, Huddinge, Täby and Lidingö the price is the same – no surcharge for the area.\n\nAn estate often reaches beyond the flat itself. We clear the basement storage, the attic storage and the garage space in the same booking, so the association or landlord gets everything back at once.",
          },
          {
            heading: "Estate clearance or estate removal?",
            content:
              "Two services that sound alike but do different things. Estate clearance, this page, is the whole process: we go through the home, set aside valuables and documents, sort what to keep, donate and recycle, and leave an empty, swept property. It suits you when nobody in the family has had time to sort, or when you want someone else to take responsibility for the whole job.\n\nEstate removal is the transport: you have already decided what should go, and we carry it out and drive it away. It suits you when the family has gone through everything and only needs help with the heavy lifting and driving.",
            cta: { text: "Read about estate removal", href: "/bortforsling-dodsbo" },
          },
          {
            heading: "Why clear the estate in Stockholm with Ungdomskraft?",
            listItems: [
              {
                label: "Nothing goes without your yes –",
                text: "If we find letters, photos, cash or papers that look important, we get in touch before we carry on. Everything personal goes into labelled boxes.",
              },
              {
                label: "We know Stockholm's buildings –",
                text: "Narrow spiral stairs on Södermalm, three-storey blocks without a lift in Hägerstensåsen, houses with full garages in Bromma. We know what takes time and plan for it.",
              },
              {
                label: "Follow the work from afar –",
                text: "Living in Gothenburg, Umeå or abroad? Book online, leave the keys with someone you trust and get photos and updates during the day.",
              },
              {
                label: "Donated first –",
                text: "What can be used again goes to Stadsmissionen, Myrorna or Erikshjälpen. When household goods go to reuse, you may be eligible for the RUT tax deduction on the labour cost.",
              },
              {
                label: "Fixed price and insured –",
                text: "You see the price when you book. All jobs are insured through Trygg-Hansa, so stairwell, lift and home are protected throughout the clearance.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does it cost to clear an estate in Stockholm?",
          answer:
            "It depends on how much is in the home and how long it takes. A garage or a couple of rooms usually matches the Extra Large package (SEK 4,995, up to 4 hours of work). A whole apartment matches Mega Large (SEK 8,995, up to 8 hours). A large house with attic and basement may need more time than that. Driving within 20 km is included. Waste fees are charged separately. You see the price as soon as you book.",
        },
        {
          question: "How is estate clearance different from estate removal?",
          answer:
            "With estate clearance we handle the whole process: going through valuables and documents, sorting, donating and recycling, and an empty, swept home at the end. Estate removal is the transport of what you have already sorted. Not sure which you need? Describe the situation when booking and we will help.",
        },
        {
          question: "Do you clean the property after an estate clearance in Stockholm?",
          answer:
            "We empty the home completely and sweep it once everything is out, so it is ready for the next step. Move-out cleaning is not part of our service. If the flat needs a deep clean before a viewing or handover, book a cleaning company for the day after, so they have an empty home to work in.",
        },
        {
          question: "Can you clear the estate if we live outside Stockholm?",
          answer:
            "Yes, we often do. You book online, send photos of the home if you can, and leave the keys with a neighbour, the estate agent or the association's property manager. During the day you get photos and updates, and if we find something that could be valuable we call before we carry on.",
        },
        {
          question: "How do you handle valuables and documents?",
          answer:
            "We go through drawers, cupboards and wardrobes and set aside anything that may matter to you or to the estate inventory – photos, letters, jewellery, cash, bank papers and insurance documents. It goes into labelled boxes for you to collect. Nothing is thrown away without your go-ahead.",
        },
        {
          question: "Do you clear the association's basement and attic storage?",
          answer:
            "Yes, in the same job. Many Stockholm associations have numbered storage units in the basement or attic, sometimes also bike rooms and garage spaces. List the spaces that belong to the home when booking, and make sure we have keys or a fob.",
        },
        {
          question: "Can you manage flats without a lift?",
          answer:
            "Yes. The three-storey blocks in Hammarbyhöjden, Hägerstensåsen and Traneberg often have no lift, and in the inner city's older buildings the lift is often too small for a sofa. We carry on the stairs, take apart what needs it and protect walls and floors. No surcharge for floors.",
        },
        {
          question: "Can you coordinate with the estate agent or landlord?",
          answer:
            "Yes. Tell us when booking when the photographer is coming, when the association will inspect or when the landlord wants the keys, and we schedule the clearance around it. We can also hand the keys to the estate agent or a neighbour when we are done, if agreed in advance.",
        },
        {
          question: "Where do the household goods go after the clearance?",
          answer:
            "Furniture, clothes and kitchenware in good condition go to a second-hand shop or charity, for example Stadsmissionen, Myrorna or Erikshjälpen. Electronics and appliances go to approved collection. The rest is sorted at approved recycling. If family members want particular items delivered to them, that can be added as an extra stop.",
        },
        {
          question: "Can I get a RUT tax deduction when you clear the estate?",
          answer:
            "If the household goods are transported to a second-hand shop or organisation for reuse, you are eligible for the RUT tax deduction on the labour cost. The deduction is applied directly on the invoice, so you never pay it upfront. A standard clearance where everything goes straight to recycling does not qualify.",
        },
        {
          question: "How long does it take to clear an estate in Stockholm?",
          answer:
            "A one- or two-room flat in the inner city usually takes half a day to a full day, depending on how much there is and how easy it is to reach with the van. A house with garage, basement and attic can take two days or more. We can also split the clearance into stages if the family wants to go through things in between.",
        },
        {
          question: "How soon can you start?",
          answer:
            "Often the same week you contact us. Many families want time to go through the personal belongings first, and we adapt to that. If a lease is ending or a viewing is booked, tell us when booking and we will prioritise the date.",
        },
        {
          question: "Do you take hazardous waste from the estate?",
          answer:
            "No, we cannot transport paint, chemicals, solvents, batteries or asbestos. As a private person you can drop them off free of charge at Stockholm Vatten och Avfall's recycling centres, for example Bromma, Lövsta, Östberga or Vantör.",
        },
        {
          question: "Which municipalities around Stockholm do you cover?",
          answer:
            "We cover the whole Stockholm region: Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. Same price everywhere, no surcharge for the area.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Clear an estate in Stockholm in 3 steps",
        step2Text:
          "We go through the home room by room, set aside what is personal and carry the rest out through stairwell and lift. What can be used again is donated, the rest recycled.",
        ctaHeading: "Book an estate clearance in Stockholm",
        ctaText:
          "Enter the address, describe the home and choose a day that suits the family. You see the price instantly – we take care of the rest with care.",
      },
    },
  },

  {
    slug: "tomma-dodsbo-goteborg",
    serviceCategory: "tömningar",
    city: "göteborg",

    meta: {
      title: "Tömma dödsbo Göteborg – Varsamt & fast pris | Ungdomskraft",
      description:
        "Tömning av dödsbo i Göteborg. Vi går igenom hemmet, sparar det viktiga och skänker resten – i Majorna, på Hisingen, i Frölunda och Mölndal. Fast pris.",
      h1: "Tömma dödsbo Göteborg",
    },

    hero: {
      heading: "Tömma dödsbo i Göteborg",
      intro:
        "Behöver ni tömma dödsbo i Göteborg? Vi hjälper till i hela staden – i ett landshövdingehus i Majorna eller Kungsladugård, en lägenhet i Guldheden eller Kortedala, ett radhus på Hisingen eller en villa i Västra Frölunda och Mölndal. Familjen bestämmer vad som ska sparas. Vi sorterar resten, skänker det som kan leva vidare och lämnar ett tomt, sopat hem.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft bär ut möbler vid tömning av dödsbo i ett landshövdingehus i Göteborg",
    },

    featureCards: {
      headingText: "Vi sköter tömningen av dödsboet i Göteborg",
      sinceText: "Dödsbotömning sedan 2014",
      bookYourText: "Boka tömning av dödsbo i Göteborg",
      bookDescText:
        "Skriv in adressen, berätta hur hemmet ser ut och välj en dag. Priset syns direkt – ingen offert att vänta på.",
      vehicleTitleText: "Skåpbil för backar och smala gator",
      vehicleDescText:
        "Vi tar oss upp för backarna i Guldheden och Masthugget och in på gårdarna i Majorna. Parkering och lastning planeras innan vi kör ut.",
    },

    itemsSection: {
      heading: "Vad vi tar hand om vid tömning av dödsbo i Göteborg",
      items: [
        {
          name: "Möbler & bohag",
          description: "Soffgrupper, sängar, byråer, matbord och hyllor",
          image: "/sofa.webp",
          alt: "Soffgrupp som bärs ut vid tömning av dödsbo i Majorna, Göteborg",
        },
        {
          name: "Kläder & textilier",
          description: "Kläder, gardiner, mattor, sängkläder och handdukar",
          image: "/bedding.webp",
          alt: "Textilier som sorteras för Stadsmissionen vid dödsbotömning i Göteborg",
        },
        {
          name: "Husgeråd & porslin",
          description: "Kök, serviser, glas, bestick och prydnadssaker",
          image: "/dishes.webp",
          alt: "Porslin som packas vid tömning av dödsbo i Guldheden, Göteborg",
        },
        {
          name: "Vitvaror",
          description: "Kyl, frys, spis, tvättmaskin och torktumlare",
          image: "/fridge.webp",
          alt: "Kylskåp som bärs ut vid tömning av dödsbo i en villa i Västra Frölunda",
        },
        {
          name: "Papper & minnen",
          description: "Dokument, brev och fotoalbum läggs undan i märkta lådor",
          image: "/box.webp",
          alt: "Kartonger med brev och fotoalbum från ett dödsbo på Hisingen, Göteborg",
        },
        {
          name: "Elektronik & övrigt",
          description: "TV, radio, verktyg, böcker och trädgårdsredskap",
          image: "/tv.webp",
          alt: "TV och elektronik från ett dödsbo i Kortedala som lämnas till e-avfall",
        },
      ],
    },

    seoContent: {
      mainHeading: "Tömning av dödsbo i Göteborg – i er takt, med respekt för det som varit",
      mainText:
        "Ett dödsbo i Göteborg kan vara en tvåa i ett landshövdingehus i Majorna, en lägenhet med utsikt i Guldheden eller ett hus på Hisingen som familjen bott i sedan sjuttiotalet. Oavsett vilket står anhöriga inför samma sak: ett helt hem som ska gås igenom, samtidigt som bouppteckningen ska förrättas inom tre månader och bostaden kanske ska säljas eller lämnas tillbaka till hyresvärden. Vi tar hand om tömningen av dödsboet så att ni kan fokusera på varandra och på de beslut som bara familjen kan fatta.\n\nGöteborgs bostäder har sina egenheter. Landshövdingehusen i Majorna, Kungsladugård, Olskroken och Landala har ett stenvåningsplan och två våningar trä ovanpå, med smala trappor och sällan någon hiss. I Guldheden, Johanneberg och Masthugget är backarna branta och parkeringen trång. I villaområdena i Västra Frölunda, Torslanda och på norra Hisingen är det i stället garage, vindar och förråd som fyllts under ett helt liv. Vi vet vad som väntar och skickar skåpbil och två personer som planerat lastningen innan de kommer.\n\nArbetet börjar alltid med en genomgång. Är ni på plats gör vi den tillsammans, annars via bilder och samtal. Det personliga – foton, brev, smycken, kontanter och papper som behövs för bouppteckningen – läggs i märkta kartonger. Det som kan leva vidare kan skänkas till Göteborgs Stadsmission, Myrorna, Erikshjälpen eller Emmaus Björkå. Resten sorteras och lämnas på godkänd återvinning. Sist sopar vi ur bostaden, så att den är redo för mäklaren, föreningen eller hyresvärden.",
      preparationHeading: "Förberedelser inför tömningen av dödsboet",
      preparationText:
        "Gå gärna igenom hemmet tillsammans i familjen först och bestäm vem som vill ha vad. Märk det som ska sparas med lappar eller ställ det i ett eget rum. Resten behöver ni inte sortera – det gör vi.\n\nTa reda på vilka utrymmen som hör till bostaden. I landshövdingehus och äldre hyreshus finns ofta vindskontor och källarförråd, i villor garage, uthus och bod. Samla nycklarna och berätta vid bokningen om någon grind, port eller bom behöver öppnas.\n\nHar ni ett datum att passa – en visning, en besiktning i föreningen eller dagen då hyresvärden ska ha tillbaka nycklarna – säg det direkt, så lägger vi tömningen i god tid före. Farligt avfall som färgrester, kemikalier och batterier kan vi inte transportera, men det tar Göteborgs återvinningscentraler emot, till exempel Alelyckan, Högsbo och Tagene.",
      additionalSections: [
        {
          heading: "Tömning av dödsbo i hela Göteborg",
          content:
            "Vi tömmer dödsbon i alla Göteborgs stadsdelar. I de centrala delarna – Linnéstaden, Vasastaden, Majorna, Kungsladugård och Olskroken – handlar det ofta om äldre hus med smala trappor och ont om plats för bilen. Där planerar vi lastningen via gården eller en tillfällig plats utanför porten.\n\nPå Hisingen – Lundby, Biskopsgården, Kärra, Tuve och Torslanda – är det ofta radhus och villor med garage och förråd. Öster om älven arbetar vi i Kortedala, Bergsjön, Gamlestaden och Kviberg, och söderut i Västra Frölunda, Tynnered och Askim. Vi tar också uppdrag i kranskommunerna Mölndal, Partille, Kungälv och Kungsbacka utan tillägg.\n\nSka flera platser tömmas, till exempel en lägenhet och ett förråd på en annan adress, lägger ni till dem som extra stopp i samma bokning.",
        },
        {
          heading: "Tömning av dödsbo – eller bara bortforsling?",
          content:
            "Vid tömning av dödsbo tar vi ansvar för helheten: genomgången av värdesaker och dokument, sorteringen i spara, skänka och återvinna, och en tom och sopad bostad när vi är klara. Det är rätt val när familjen inte har hunnit eller orkat sortera.\n\nHar ni redan gått igenom allt och vet exakt vad som ska bort? Då räcker det med bortforsling av dödsbo – vi bär ut det ni pekar på och kör iväg det, utan genomgång.",
          cta: { text: "Till bortforsling av dödsbo", href: "/bortforsling-dodsbo" },
        },
        {
          heading: "Varför tömma dödsboet i Göteborg med Ungdomskraft?",
          listItems: [
            {
              label: "Genomgång innan något bärs ut –",
              text: "Vi letar igenom lådor och skåp efter det som kan betyda något. Hittar vi kontanter, brev eller papper ringer vi er innan vi fortsätter.",
            },
            {
              label: "Vana vid Göteborgs hus –",
              text: "Trätrapporna i landshövdingehusen, backarna i Guldheden och de fulla garagen på Hisingen. Vi planerar tid och bärning efter hur huset faktiskt ser ut.",
            },
            {
              label: "Enkelt för anhöriga på distans –",
              text: "Bor ni i Stockholm, Malmö eller utomlands? Boka online, lämna nycklarna hos en granne eller mäklaren och få bilder under dagen.",
            },
            {
              label: "Återbruk i första hand –",
              text: "Möbler, kläder och husgeråd som kan användas igen kan gå till Stadsmissionen, Myrorna, Erikshjälpen eller Emmaus Björkå. Går bohaget till återanvändning kan ni ha rätt till RUT-avdrag på arbetskostnaden.",
            },
            {
              label: "Priset klart när ni bokar –",
              text: "Inga offerter och inga tillägg för stadsdel. Alla uppdrag är försäkrade genom Trygg-Hansa.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar tömning av dödsbo i Göteborg?",
        answer:
          "Priset följer mängden och tiden. Ett par rum eller ett fullt garage motsvarar oftast Extra Stor (4 995 kr, upp till 4 timmar). En hel lägenhet motsvarar Mega Stor (8 995 kr, upp till 8 timmar). Ett hus med vind, källare och uthus kan kräva mer tid. Körsträcka inom 20 km ingår, avfallsavgifter debiteras separat, och priset syns direkt i bokningen.",
      },
      {
        question: "Vad är skillnaden mot bortforsling av dödsbo?",
        answer:
          "Vid tömning av dödsbo går vi igenom hemmet, lägger undan värdesaker och dokument, sorterar och skänker, och lämnar en tom bostad. Bortforsling av dödsbo är enbart att bära ut och köra iväg det ni redan har sorterat. Beskriv läget när ni bokar så hjälper vi er välja.",
      },
      {
        question: "Städar ni bostaden efter tömningen?",
        answer:
          "Vi sopar ur bostaden när allt är ute, men flyttstädning ingår inte i vår tjänst. Behöver bostaden storstädas inför visning eller besiktning, boka en städfirma till efter vår tömning så har de tomt att arbeta i.",
      },
      {
        question: "Kan ni tömma dödsboet i Göteborg om vi bor i en annan stad?",
        answer:
          "Ja. Boka online, skicka gärna några bilder och lämna nycklarna hos en granne, mäklaren eller en förvaltare. Vi skickar uppdateringar under dagen och ringer om vi hittar något som kan vara värdefullt eller personligt.",
      },
      {
        question: "Vad gör ni med brev, foton och dokument?",
        answer:
          "Allt som ser personligt ut eller kan behövas för bouppteckningen – brev, fotoalbum, smycken, kontanter, bankpapper och avtal – läggs i märkta kartonger. Ni hämtar dem, eller så lägger vi till en leverans till någon i familjen. Ingenting sådant slängs.",
      },
      {
        question: "Klarar ni landshövdingehus utan hiss?",
        answer:
          "Ja, det är vardag för oss i Majorna, Kungsladugård och Olskroken. Trätrapporna är smala och svänger, så vi monterar isär större möbler och skyddar väggar och trappsteg. Inga tillägg för våningsplan.",
      },
      {
        question: "Tömmer ni vindskontor, källare och garage också?",
        answer:
          "Ja, allt som hör till bostaden töms i samma uppdrag – vindskontor, källarförråd, garage, uthus och trädgårdsbod. Ange utrymmena när ni bokar så planerar vi tiden rätt.",
      },
      {
        question: "Kan ni anpassa tömningen efter mäklare och hyresvärd?",
        answer:
          "Ja. Berätta när fotograferingen, besiktningen eller nyckelöverlämningen är planerad, så lägger vi tömningen före. Om ni vill kan vi lämna nycklarna till mäklaren eller en granne när vi är klara.",
      },
      {
        question: "Vart går bohaget efter tömningen i Göteborg?",
        answer:
          "Det som kan användas igen kan gå till Göteborgs Stadsmission, Myrorna, Erikshjälpen eller Emmaus Björkå. Elektronik och vitvaror lämnas till godkänd mottagning, och resten sorteras på godkänd återvinning.",
      },
      {
        question: "Kan jag få RUT-avdrag på tömning av dödsbo i Göteborg?",
        answer:
          "Om bohaget transporteras till en secondhand-butik eller organisation för återanvändning har du rätt till RUT-avdrag på arbetskostnaden. Avdraget hanteras direkt på fakturan, så du behöver aldrig ligga ute med pengarna. Vanlig tömning där allt körs direkt till återvinning ger inte rätt till avdrag.",
      },
      {
        question: "Hur lång tid tar det?",
        answer:
          "En lägenhet tar oftast en halv till en hel dag. Ett hus med garage och vind kan ta två dagar eller mer. Vill familjen gå igenom saker i omgångar delar vi upp tömningen i flera besök.",
      },
      {
        question: "Hur snabbt kan ni komma?",
        answer:
          "Oftast samma vecka. Har ni en tidsgräns – ett hyresavtal som löper ut eller en visning – säg det när ni bokar så prioriterar vi. Vill ni ha tid att gå igenom de personliga sakerna först väntar vi gärna.",
      },
      {
        question: "Tar ni farligt avfall från dödsboet?",
        answer:
          "Nej, färg, kemikalier, batterier och asbest kan vi inte transportera. Sådant lämnar du på någon av Göteborgs återvinningscentraler, till exempel Alelyckan, Högsbo, Sävenäs eller Tagene.",
      },
      {
        question: "Vilka kommuner i Göteborgsregionen tömmer ni dödsbon i?",
        answer: `Vi finns i hela Göteborgsregionen: ${goteborgKommunerText}. Samma pris överallt, inga tillägg för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Tömma dödsbo i Göteborg i 3 steg",
      step2Text:
        "Vi går igenom hemmet med er, lägger undan det personliga och bär ut resten – även i trätrapporna i landshövdingehusen. Det som kan leva vidare skänks.",
      ctaHeading: "Boka tömning av dödsbo i Göteborg",
      ctaText:
        "Fyll i adressen, beskriv bostaden och välj en dag. Priset syns direkt, och vi tar hand om resten varsamt.",
    },

    en: {
      slug: "house-clearance-gothenburg",
      meta: {
        title: "House Clearance Gothenburg – Estate Clearing | Ungdomskraft",
        description:
          "Estate clearance in Gothenburg. We go through the home, keep what matters and donate the rest – in Majorna, on Hisingen, in Frölunda and Mölndal. Fixed price.",
        h1: "House Clearance Gothenburg",
      },
      hero: {
        heading: "Estate clearance in Gothenburg",
        intro:
          "Need to clear an estate in Gothenburg? We help across the whole city – in a landshövdingehus in Majorna or Kungsladugård, a flat in Guldheden or Kortedala, a terraced house on Hisingen or a villa in Västra Frölunda and Mölndal. The family decides what to keep. We sort the rest, donate what can live on and leave an empty, swept home.",
        heroImageAlt:
          "Ungdomskraft carrying out furniture during an estate clearance in a landshövdingehus in Gothenburg",
      },
      itemsSection: {
        heading: "What we take care of in an estate clearance in Gothenburg",
        items: [
          {
            name: "Furniture & household",
            description: "Sofa sets, beds, chests of drawers, dining tables and shelves",
            image: "/sofa.webp",
            alt: "Sofa set carried out during an estate clearance in Majorna, Gothenburg",
          },
          {
            name: "Clothes & textiles",
            description: "Clothes, curtains, rugs, bed linen and towels",
            image: "/bedding.webp",
            alt: "Textiles sorted for Stadsmissionen during an estate clearance in Gothenburg",
          },
          {
            name: "Kitchenware & china",
            description: "Kitchen items, dinner sets, glasses, cutlery and ornaments",
            image: "/dishes.webp",
            alt: "China packed during an estate clearance in Guldheden, Gothenburg",
          },
          {
            name: "Appliances",
            description: "Fridge, freezer, cooker, washing machine and dryer",
            image: "/fridge.webp",
            alt: "Fridge carried out during an estate clearance in a house in Västra Frölunda",
          },
          {
            name: "Papers & keepsakes",
            description: "Documents, letters and photo albums set aside in labelled boxes",
            image: "/box.webp",
            alt: "Boxes of letters and photo albums from an estate on Hisingen, Gothenburg",
          },
          {
            name: "Electronics & other",
            description: "TV, radio, tools, books and garden equipment",
            image: "/tv.webp",
            alt: "TV and electronics from an estate in Kortedala taken to e-waste collection",
          },
        ],
      },
      seoContent: {
        mainHeading: "Estate clearance in Gothenburg – at your pace, with respect for what has been",
        mainText:
          "An estate in Gothenburg might be a two-room flat in a landshövdingehus in Majorna, a flat with a view in Guldheden or a house on Hisingen that the family has lived in since the seventies. Whatever it is, relatives face the same thing: a whole home to go through, while the estate inventory has to be held within three months and the home may need to be sold or handed back to the landlord. We take care of the clearance so you can focus on each other and on the decisions only the family can make.\n\nGothenburg's homes have their quirks. The landshövdingehus buildings in Majorna, Kungsladugård, Olskroken and Landala have a stone ground floor with two storeys of timber on top, narrow stairs and rarely a lift. In Guldheden, Johanneberg and Masthugget the hills are steep and parking tight. In the villa areas of Västra Frölunda, Torslanda and northern Hisingen it is the garages, attics and storage rooms filled over a lifetime. We know what to expect and send a van and two people who have planned the loading before they arrive.\n\nThe work always starts with a walk-through. If you are there we do it together, otherwise via photos and calls. Personal items – photos, letters, jewellery, cash and papers needed for the estate inventory – go into labelled boxes. What can live on can be donated to Göteborgs Stadsmission, Myrorna, Erikshjälpen or Emmaus Björkå. The rest is sorted and delivered to approved recycling. Finally we sweep the home so it is ready for the estate agent, the association or the landlord.",
        preparationHeading: "Preparing for the estate clearance",
        preparationText:
          "If you can, go through the home together as a family first and decide who wants what. Mark what should be kept with notes or put it in a room of its own. The rest you don't need to sort – we do that.\n\nFind out which spaces belong to the home. Landshövdingehus buildings and older rental blocks often have attic and basement storage, houses a garage, outbuilding and shed. Gather the keys and tell us when booking if a gate, door or barrier needs opening.\n\nIf there is a date to meet – a viewing, an inspection by the association or the day the landlord wants the keys back – tell us straight away and we will schedule the clearance well before. We cannot transport hazardous waste such as leftover paint, chemicals and batteries, but Gothenburg's recycling centres accept it, for example Alelyckan, Högsbo and Tagene.",
        additionalSections: [
          {
            heading: "Estate clearance across Gothenburg",
            content:
              "We clear estates in every part of Gothenburg. In the central areas – Linnéstaden, Vasastaden, Majorna, Kungsladugård and Olskroken – it is often older buildings with narrow stairs and little room for the van. There we plan the loading via the courtyard or a temporary spot outside the door.\n\nOn Hisingen – Lundby, Biskopsgården, Kärra, Tuve and Torslanda – it is often terraced houses and villas with garages and storage. East of the river we work in Kortedala, Bergsjön, Gamlestaden and Kviberg, and to the south in Västra Frölunda, Tynnered and Askim. We also take jobs in the neighbouring municipalities of Mölndal, Partille, Kungälv and Kungsbacka with no surcharge.\n\nIf several places need clearing, for example a flat and a storage unit at another address, add them as extra stops in the same booking.",
          },
          {
            heading: "Estate clearance – or just removal?",
            content:
              "With estate clearance we take responsibility for the whole: going through valuables and documents, sorting into keep, donate and recycle, and an empty, swept home when we are done. It is the right choice when the family has not had the time or energy to sort.\n\nAlready been through everything and know exactly what should go? Then estate removal is enough – we carry out what you point to and drive it away, without a walk-through.",
            cta: { text: "Go to estate removal", href: "/bortforsling-dodsbo" },
          },
          {
            heading: "Why clear the estate in Gothenburg with Ungdomskraft?",
            listItems: [
              {
                label: "A walk-through before anything goes –",
                text: "We search drawers and cupboards for anything that might matter. If we find cash, letters or papers we call you before we continue.",
              },
              {
                label: "Used to Gothenburg's buildings –",
                text: "The timber stairs of the landshövdingehus, the hills of Guldheden and the full garages on Hisingen. We plan time and carrying around how the building really is.",
              },
              {
                label: "Easy for relatives far away –",
                text: "Living in Stockholm, Malmö or abroad? Book online, leave the keys with a neighbour or the estate agent and get photos during the day.",
              },
              {
                label: "Reuse first –",
                text: "Furniture, clothes and kitchenware that can be used again can go to Stadsmissionen, Myrorna, Erikshjälpen or Emmaus Björkå. When household goods go to reuse, you may be eligible for the RUT tax deduction on the labour cost.",
              },
              {
                label: "Price settled when you book –",
                text: "No quotes and no surcharge for any district. All jobs are insured through Trygg-Hansa.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does an estate clearance in Gothenburg cost?",
          answer:
            "The price follows the amount and the time. A couple of rooms or a full garage usually matches Extra Large (SEK 4,995, up to 4 hours). A whole flat matches Mega Large (SEK 8,995, up to 8 hours). A house with attic, basement and outbuildings may need more time. Driving within 20 km is included, waste fees are charged separately, and the price shows directly in the booking.",
        },
        {
          question: "What is the difference from estate removal?",
          answer:
            "With estate clearance we go through the home, set aside valuables and documents, sort and donate, and leave an empty home. Estate removal is only carrying out and driving away what you have already sorted. Describe the situation when booking and we will help you choose.",
        },
        {
          question: "Do you clean the home after the clearance?",
          answer:
            "We sweep the home once everything is out, but move-out cleaning is not part of our service. If the home needs a deep clean before a viewing or inspection, book a cleaning company for after our clearance so they have an empty space to work in.",
        },
        {
          question: "Can you clear the estate in Gothenburg if we live in another city?",
          answer:
            "Yes. Book online, send a few photos if you can and leave the keys with a neighbour, the estate agent or a property manager. We send updates during the day and call if we find anything that could be valuable or personal.",
        },
        {
          question: "What do you do with letters, photos and documents?",
          answer:
            "Anything that looks personal or may be needed for the estate inventory – letters, photo albums, jewellery, cash, bank papers and contracts – goes into labelled boxes. You collect them, or we add a delivery to someone in the family. Nothing like that is thrown away.",
        },
        {
          question: "Can you manage landshövdingehus buildings without a lift?",
          answer:
            "Yes, that is everyday work for us in Majorna, Kungsladugård and Olskroken. The timber stairs are narrow and turn, so we take larger furniture apart and protect walls and steps. No surcharge for floors.",
        },
        {
          question: "Do you clear attic storage, basements and garages too?",
          answer:
            "Yes, everything that belongs to the home is cleared in the same job – attic storage, basement storage, garage, outbuilding and garden shed. List the spaces when booking and we will plan the time accordingly.",
        },
        {
          question: "Can you fit the clearance around the estate agent and landlord?",
          answer:
            "Yes. Tell us when the photos, inspection or key handover are planned and we will schedule the clearance before. If you like, we can hand the keys to the estate agent or a neighbour when we are done.",
        },
        {
          question: "Where do the household goods go after the clearance in Gothenburg?",
          answer:
            "What can be used again can go to Göteborgs Stadsmission, Myrorna, Erikshjälpen or Emmaus Björkå. Electronics and appliances go to approved collection, and the rest is sorted at approved recycling.",
        },
        {
          question: "Can I get a RUT tax deduction on an estate clearance in Gothenburg?",
          answer:
            "If the household goods are transported to a second-hand shop or organisation for reuse, you are eligible for the RUT tax deduction on the labour cost. The deduction is applied directly on the invoice, so you never pay it upfront. A standard clearance where everything goes straight to recycling does not qualify.",
        },
        {
          question: "How long does it take?",
          answer:
            "A flat usually takes half a day to a full day. A house with garage and attic can take two days or more. If the family wants to go through things in rounds, we split the clearance into several visits.",
        },
        {
          question: "How quickly can you come?",
          answer:
            "Usually the same week. If there is a deadline – a lease ending or a viewing – tell us when booking and we will prioritise it. If you want time to go through the personal belongings first, we are happy to wait.",
        },
        {
          question: "Do you take hazardous waste from the estate?",
          answer:
            "No, we cannot transport paint, chemicals, batteries or asbestos. You can drop them off at one of Gothenburg's recycling centres, for example Alelyckan, Högsbo, Sävenäs or Tagene.",
        },
        {
          question: "Which municipalities in the Gothenburg region do you cover?",
          answer:
            "We cover the whole Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. Same price everywhere, no surcharge for neighbouring municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Clear an estate in Gothenburg in 3 steps",
        step2Text:
          "We go through the home with you, set aside what is personal and carry out the rest – even on the timber stairs of the landshövdingehus. What can live on is donated.",
        ctaHeading: "Book an estate clearance in Gothenburg",
        ctaText:
          "Enter the address, describe the home and choose a day. The price shows instantly, and we take care of the rest with care.",
      },
    },
  },
];
